/**
 * Defect tab — right panel and shared constants for the visual editor's
 * defect mode. Extracted from visual-style-file-page.tsx.
 *
 * Exports `ZONE_MAP`, `SEVERITY`, and `DefectPin` so the parent page can
 * render `DraggablePin` overlays on the canvas and this file only owns the
 * inspector panel.
 */
import { ArrowLeft, TriangleAlert } from "lucide-react";
import { StatusPill, type StatusTone } from "@flint/ui";
import type { qc as qcContract } from "@flint/api-contract";
import { KV, PhotoSlot, Tile } from "./canvas-helpers.tsx";

/* ── Types ────────────────────────────────────────────────────────────── */

export type QcDefect =
  qcContract.QcViewWire["lots"][number]["defects"][number];

export interface DefectPin extends QcDefect {
  label: string;
  lotRef: string;
  /** Zone-anchored fallback x (% of stage). */
  anchorX: number;
  /** Zone-anchored fallback y (% of stage). */
  anchorY: number;
}

/* ── Constants ────────────────────────────────────────────────────────── */

/** QC zone string → fallback pin anchor (% of 400×500 stage). */
export const ZONE_MAP: Record<string, { x: number; y: number }> = {
  "Body front": { x: 50, y: 56 },
  "Body back": { x: 50, y: 56 },
  "Left sleeve": { x: 13, y: 42 },
  "Right sleeve": { x: 87, y: 42 },
  "Collar / neck": { x: 50, y: 21 },
  Hem: { x: 50, y: 89 },
  Shoulder: { x: 33, y: 23 },
  "Side seam": { x: 20, y: 62 },
};
export const UNZONED = { x: 50, y: 48 };

export const SEVERITY: Record<
  QcDefect["severity"],
  { tone: StatusTone; label: string; dot: string; ring: string }
> = {
  critical: {
    tone: "bad",
    label: "Critical",
    dot: "bg-bad",
    ring: "ring-bad/30",
  },
  major: { tone: "bad", label: "Major", dot: "bg-bad", ring: "ring-bad/30" },
  minor: {
    tone: "warn",
    label: "Minor",
    dot: "bg-warn",
    ring: "ring-warn/30",
  },
};

/* ── Panel ────────────────────────────────────────────────────────────── */

export function DefectPanel({
  pin,
  pins,
  dhuTenths,
  rftPercent,
  blocked,
  unplaced,
  onPick,
  onBack,
  uploading,
  onUploadPhoto,
}: {
  pin: DefectPin | null;
  pins: DefectPin[];
  dhuTenths: number;
  rftPercent: number;
  blocked: boolean;
  unplaced: number;
  onPick: (id: string) => void;
  onBack: () => void;
  uploading: boolean;
  onUploadPhoto: (file: File) => void;
}) {
  if (pin) {
    const sev = SEVERITY[pin.severity];
    const placed = pin.pinX !== null && pin.pinY !== null;
    return (
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <span
            className={`grid h-7 w-7 place-items-center rounded-full text-xs font-bold text-paper ${sev.dot}`}
          >
            {pin.label}
          </span>
          <h3 className="flex-1 text-sm font-semibold text-ink">{pin.defectType}</h3>
          <button
            type="button"
            onClick={onBack}
            aria-label="Back to defect list"
            className="grid h-7 w-7 shrink-0 place-items-center rounded-sm border border-line text-ink-2 transition hover:bg-bg"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
          </button>
        </div>
        <dl className="flex flex-col gap-1.5 text-sm">
          <KV k="Zone" v={pin.zone ?? "Unzoned"} />
          <KV
            k="Severity"
            v={
              <StatusPill tone={sev.tone} size="sm" dot>
                {sev.label}
              </StatusPill>
            }
          />
          <KV k="Qty" v={`${pin.qty} pc${pin.qty === 1 ? "" : "s"}`} />
          <KV
            k="Disposition"
            v={<span className="capitalize">{pin.disposition}</span>}
          />
          <KV
            k="Lot"
            v={<span className="font-mono">{pin.lotRef}</span>}
          />
          <KV
            k="Placed"
            v={
              placed ? (
                <span className="capitalize text-ink-2">
                  {pin.pinView} · {pin.pinX?.toFixed(0)},{pin.pinY?.toFixed(0)}
                </span>
              ) : (
                <span className="text-muted">Not yet — drag it</span>
              )
            }
          />
          {pin.notes ? <KV k="Notes" v={pin.notes} /> : null}
        </dl>
        <PhotoSlot
          photoUrl={pin.photoUrl}
          uploading={uploading}
          onUpload={onUploadPhoto}
        />
        <p className="text-xs text-muted">
          The finding is edited in QC §7 (append-only); the pin position and
          photo are set here.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <h3 className="text-sm font-semibold text-ink">Defects</h3>
      {blocked ? (
        <div className="flex items-center gap-2 rounded-sm border border-bad-100 border-l-[3px] border-l-bad bg-bad-50 px-2.5 py-2 text-xs font-medium text-bad">
          <TriangleAlert size={13} /> A failed lot is blocking dispatch.
        </div>
      ) : null}
      <div className="grid grid-cols-2 gap-2">
        <Tile
          label="DHU"
          value={(dhuTenths / 10).toFixed(1)}
          sub="per 100"
        />
        <Tile label="RFT" value={`${rftPercent}%`} sub="right first time" />
      </div>
      {unplaced > 0 ? (
        <p className="rounded-sm border border-dashed border-line bg-bg px-2.5 py-2 text-xs text-muted">
          {unplaced} pin{unplaced === 1 ? "" : "s"} not placed yet — drag{" "}
          {unplaced === 1 ? "it" : "them"} from the body onto the exact spot.
        </p>
      ) : null}
      {pins.length > 0 ? (
        <ul className="flex flex-col gap-1">
          {pins.map((p) => {
            const sev = SEVERITY[p.severity];
            return (
              <li key={p.id}>
                <button
                  type="button"
                  onClick={() => onPick(p.id)}
                  className="flex w-full items-center gap-2 rounded-sm border border-line bg-paper px-2 py-1.5 text-left text-xs transition hover:bg-bg"
                >
                  <span
                    className={`grid h-5 w-5 shrink-0 place-items-center rounded-full text-xs font-bold text-paper ${sev.dot}`}
                  >
                    {p.label}
                  </span>
                  <span className="min-w-0 flex-1 truncate font-medium text-ink">
                    {p.defectType}
                  </span>
                  <span className="shrink-0 text-muted">
                    {p.pinView ?? (p.zone ?? "—")}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="text-xs text-muted">
          Click a pin on the garment to inspect it. Defects are recorded in QC
          §7.
        </p>
      )}
    </div>
  );
}
